import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import DataType from 'scoreboard/datasets/model/data-type'
const { DATASET_LINK, CLUSTER } = DataType

/**
 * Creates a filter for dataset fields to find those eligible to be used in forms
 * @param {Function} usedFieldsProvider provide a list of "used" field ids. These
 * will also be filtered from the final result
 * @param {Boolean} allowLinkedFields when false, filters out any fields that are
 * of the type 'dataset-link'. Defaults to true
 */
function datasetFieldFilter(usedFieldsProvider = null, allowLinkedFields = true) {
	return (field, dataset) => {
		// Note: need to get fresh "usedFieldIds" in here because fields
		// could have been added to the form after initial rendering
		let usedFieldIds = usedFieldsProvider ? usedFieldsProvider() : []

		// only allow using a field once
		if (usedFieldIds[field.id]) return false

		// #40019 Cannot be manually entered so should not be edited
		if (field.dataType === CLUSTER) return false

		// Rules from #34007

		// #34631 removes rule from #34007 - fields that are non-editable
		// can now be added to a form, but will shown in a disabled state

		// Can't pick non-native fields (ie. visibleLinkedFields)
		// Per #41273 we added the ability to add linked fields, but we should
		// hide the manually added/deleted field
		if (dataset.isManuallyAddedFieldId(field.id)) return false

		// This is a basic field or a field belonging to this dataset that is
		// a dataset-link type
		if (field.datasetId === dataset.get('id') && (allowLinkedFields || field.dataType !== DATASET_LINK)) {
			return true
		}

		if (!allowLinkedFields) return false

		// This is the inverse field of a field linked by a junction dataset
		if (isJunctionLinkField(field, dataset)) return true

		// This field is not directly linked to this dataset
		if (field.linkedDatasetId !== dataset.get('id')) return false

		const linkedDataset = dataset.datasetsById[field.datasetId]
		// This dataset is a junction. We never want to show these fields
		if (linkedDataset.get('isJunctionDataset')) return false

		return true
	}
}

/**
 * Checks if this field is linked to this dataset via a junction dataset.
 * This requires getting the field's dataset, finding that dataset's junction dataset,
 * then checking if the other dataset in the junction is this dataset
 * (the one being edited in the form)
 *
 * Note: This is very similar logic to FormsDao#isDatasetLink (not exactly), so
 * if changes are made in determining a junction dataset link, they need to
 * change here and there
 */
function isJunctionLinkField(field, dataset) {
	// This field belongs to this dataset, so it can't be from the other dataset
	if (field.datasetId === dataset.id) return false

	const linkedDataset = dataset.datasetsById[field.datasetId]
	if (_.isNil(linkedDataset)) return false

	// We only link on the primary key
	if (linkedDataset.get('primaryKeyFieldId') !== field.id) return false
	// This dataset is a junction. We never want to show these fields
	if (linkedDataset.get('junctionDataset')) return false

	const junctionDatasets = dataset.get('junctionDatasets')
	if (!junctionDatasets) return false

	const links = _.filter(junctionDatasets, j => j.datasetId == field.datasetId)

	// No junction dataset for this field
	if (!links.length) return false

	if (links.find(l => l.linkedDatasetId == dataset.id)) return true

	return false
}

/**
 * Creates groups by dataset for all provided fields if they are not direct fields
 * of the provided dataset. For use in a select dropdown
 */
function assignGroupsForLinkedFields(dataset, fields) {
	const groups = {}
	_.forEach(fields, f => {
		// This field belongs to this dataset
		if (f.datasetId === dataset.get('id')) return

		groups[f.id] = i18n.get('datasets.fields.fromLinkedDatasets.to', dataset.get('label'))
	})
	return groups
}

export { datasetFieldFilter, assignGroupsForLinkedFields, isJunctionLinkField }